import { UseMutateFunction } from '@tanstack/react-query';
import React, { FC, ReactElement, useCallback, useEffect } from 'react';
import { useWatch } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import { HttpException } from '../../../../exceptions/index';
import { Stakeholder as StakeholderHooks, useLocale, useReactForm } from '../../../../hooks/index';
import {
  CompanyStakeholderDto,
  HumanStakeholderDto,
  Stakeholder,
} from '../../../../types/stakeholder.types';
import { getS3FileOriginalName } from '../../../../utils/get-s3file-original-name.util';
import { handleFormInvalid } from '../../../../utils/handle-form-invalid.util';
import { transformDateToISOString } from '../../stakeholders';
import { ChildrenFormProps, EditStakeholderVariant } from '../stakeholder-actions.types';
import { FormSchema, formSchema } from '../stakeholder-actions.validation';
import { EditPreview, StepOne, StepTwo } from '../steps/index';

export type EditStakeholderFormProps = {
  defaultData: Stakeholder | undefined;
  isEditing: boolean;
  mutateStakeholder: UseMutateFunction<
    unknown,
    HttpException,
    CompanyStakeholderDto | HumanStakeholderDto
  >;
  onEditClick: (props: { id: string; step: EditStakeholderVariant }) => void;
  setCloseModal: () => void;
  stakeholderId: string;
  step: EditStakeholderVariant;
};

const formSteps: ((
  props: {
    onEditClick: (step: EditStakeholderVariant) => void;
  } & ChildrenFormProps,
) => ReactElement)[] = [
  (props) => <StepOne {...props} />,
  (props) => <StepTwo {...props} />,
  (props) => <EditPreview {...props} />,
];

const EditStakeholderForm: FC<EditStakeholderFormProps> = ({
  isEditing,
  mutateStakeholder,
  defaultData,
  setCloseModal,
  onEditClick,
  stakeholderId,
  step,
}) => {
  const { messagesLocale } = useLocale();
  const {
    control,
    setValue,
    handleSubmit,
    resetField,
    reset,
    getValues,
    setError,
    getFieldState,
    clearErrors,
    formState: { disabled, errors },
  } = useReactForm({
    schema: formSchema,
    disabled: !isEditing,
  });

  const email = useWatch({ control, name: 'stepOne.email' });
  const { stakeholder: stakeholderByEmail, isLoading } = StakeholderHooks.useStakeholderByEmail({
    email,
  });

  useEffect(() => {
    if (!stakeholderByEmail) return;

    if (stakeholderByEmail.accessRole)
      return setError('stepOne.email', {
        message: messagesLocale['EMAIL_TAKEN_NEED_TO_CONVERT_GUEST_USER'],
      });
  }, [messagesLocale, setError, stakeholderByEmail]);

  const submitHandler = useCallback(
    (data: FormSchema) => {
      const { stepOne, stepTwo } = data;
      const {
        address,
        email,
        fullName,
        stakeholderType,
        phoneNumber,
        access,
        phoneCountry,
        human,
        company,
        subEmails,
      } = stepOne;
      if (!human || !company) return;
      const { birthdayDate, nationality, passportExpiryDate, passportNo } = human;

      const { identifier, incorporationCountry, incorporationDate } = company;

      mutateStakeholder(
        {
          countryOfInc: incorporationCountry?.iso2Code || undefined,
          stakeholderCompanyId: identifier,
          address: address,
          incDate: incorporationDate
            ? transformDateToISOString(
                incorporationDate.year,
                incorporationDate.month + 1,
                incorporationDate.day,
              )
            : undefined,
          birthday: birthdayDate
            ? transformDateToISOString(birthdayDate.year, birthdayDate.month + 1, birthdayDate.day)
            : undefined,
          email,
          fullName,
          nationality: nationality?.iso2Code || undefined,
          passportExpDate: passportExpiryDate
            ? transformDateToISOString(
                passportExpiryDate.year,
                passportExpiryDate.month + 1,
                passportExpiryDate.day,
              )
            : undefined,
          passportNo: passportNo.toString(),
          type: stakeholderType,
          phone: phoneCountry?.prefix ? phoneCountry?.prefix + phoneNumber : undefined,
          phoneCountryIso2: phoneCountry?.iso2Code || undefined,
          capTableAccess: access?.capTable,
          positionAccess: access?.position,
          capitalChangeAccess: access?.equity,
          companyTableAccess: access?.companyTable,
          additionalNotes: stepTwo?.additionalNotes,
          subEmails: subEmails.map(({ value }) => value),
          filesLinks:
            stepTwo?.files?.reduce<string[]>((prev, curr) => [...prev, curr.docLink], []) || [],
        },
        {
          onSuccess: () => setCloseModal(),
        },
      );

      return;
    },
    [mutateStakeholder, setCloseModal],
  );

  useEffect(() => {
    if (!defaultData) return;

    const files: {
      abort: () => void;
      doc: {
        name: string;
        size: number;
        type: string;
      };
      docLink: string;
      id: string;
      loadProgress: 100;
    }[] = defaultData?.filesLinks?.map((link) => {
      const originalFileName = getS3FileOriginalName(link);

      return {
        abort: () => '',
        loadProgress: 100,
        doc: {
          name: originalFileName || '',
          size: 10,
          type: 'application/pdf',
        },
        docLink: link,
        id: uuidv4(),
      };
    });

    reset({
      stepOne: {
        email: defaultData?.email,
        subEmails: defaultData.subEmails?.map((subEmail) => ({ value: subEmail })),
        fullName: defaultData?.fullName,
        address: defaultData?.address || undefined,
        phoneNumber: defaultData?.phone || undefined,
        phoneCountry: {
          iso2Code: defaultData?.phoneCountryIso2 || '',
          name: '',
          prefix: '',
        },
        access: {
          capTable: defaultData?.capTableAccess,
          position: defaultData?.positionAccess,
          equity: defaultData?.capitalChangeAccess,
          companyTable: defaultData?.companyTableAccess,
        },
        stakeholderType: defaultData?.type,
        human: {
          birthdayDate: defaultData?.birthday
            ? {
                day: new Date(defaultData?.birthday).getDate(),
                month: new Date(defaultData?.birthday).getMonth(),
                year: new Date(defaultData?.birthday).getFullYear(),
              }
            : undefined,
          nationality: defaultData?.nationalityCountry
            ? {
                iso2Code: defaultData?.nationalityCountry.iso2,
                name: defaultData?.nationalityCountry.name,
              }
            : undefined,
          passportExpiryDate: defaultData?.passportExpDate
            ? {
                day: new Date(defaultData?.passportExpDate).getDate(),
                month: new Date(defaultData?.passportExpDate).getMonth(),
                year: new Date(defaultData?.passportExpDate).getFullYear(),
              }
            : undefined,
          passportNo: defaultData?.passportNo || undefined,
        },
        company: {
          identifier: defaultData?.stakeholderCompanyId || undefined,
          incorporationCountry: defaultData?.countryOfInc
            ? {
                iso2Code: defaultData.countryOfInc.iso2,
                name: defaultData?.countryOfInc.name,
              }
            : undefined,
          incorporationDate: defaultData?.incDate
            ? {
                day: new Date(defaultData?.incDate).getDate(),
                month: new Date(defaultData?.incDate).getMonth(),
                year: new Date(defaultData?.incDate).getFullYear(),
              }
            : undefined,
        },
      },
      stepTwo: {
        additionalNotes: defaultData?.additionalNotes || undefined,
        files: files,
      },
    });
  }, [defaultData, reset]);

  return (
    <div className="flex h-full flex-col gap-4 overflow-hidden">
      {formSteps[step - 1]({
        setError,
        formData: getValues,
        nextFormStep: () => handleSubmit(submitHandler, handleFormInvalid)(),
        prevFormStep: () => {},
        setFormData: setValue,
        control,
        handleCloseModal: setCloseModal,
        filedState: getFieldState,
        errors,
        clearErrors,
        lockMode: true,
        previewMode: !isEditing,
        isLoading,
        resetField,
        disabled,
        onEditClick: (step) => onEditClick({ id: stakeholderId, step }),
        stakeholder: defaultData || null,
      })}
    </div>
  );
};

export default EditStakeholderForm;
